import React, { useEffect, useState } from "react";
import { lucideIconForAPICategory } from "../../../../models/Helpers";
import {
  DOCS_API_REF_ASSOCIATIONS_PATH,
  DOCS_API_REF_ASSOCIATION_TYPES_PATH,
  DOCS_API_REF_CUSTOM_OBJECTS_PATH,
  DOCS_API_REF_CUSTOM_OBJECT_CLASSES_PATH,
  DOCS_CUSTOM_OBJECTS_PATH,
} from "../../../../router/RouterUtils";
import { SectionHeaderWrapper } from "../../../shared/SectionHeaderWrapper";
import {
  fetchCustomObjectsCommonModelsFromCategory,
  updateCustomObjectCommonModel,
} from "../../../../api-client/categories/CommonModelTogglesAPIClient";
import CommonModel from "../../../../models/CommonModel";
import { showErrorToast } from "../../../shared/Toasts";
import {
  Badge,
  Toggle,
  Link,
  Text,
  Card,
  displayNameForAPICategory,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import { APICategory } from "../../../../models/Entities";

/**
 * Add categories here if you want them to display as 'Coming soon'. If isActive is set to false, they will display the 'Coming soon' tag.
 * If isActive is set to True, users will be able to toggle Custom Object scopes for that feature.
 */

type CategoryIsActiveConfig = {
  isActive: boolean;
};

const CategoryDisplayConfig: Partial<Record<APICategory, CategoryIsActiveConfig>> = {
  [APICategory.crm]: {
    isActive: true,
  },
};

const ConfigurationSyncingCustomObjects = () => {
  /**
   * Add backend for Custom Objects and activate here. Using as a placeholder currently
   */
  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(false);
  const [commonModels, setCommonModels] = useState<CommonModel[]>([]);

  useEffect(() => {
    fetchCustomObjectsCommonModelsFromCategory().then((result) => {
      if (result.status === "success") {
        setDataHasLoaded(true);
        return setCommonModels(result.data);
      }
    });
  }, []);

  /**
   * Function that takes in a category and returns whether or not the Custom Objects Common Models are activated or not
   * It does this by checking if the model has enabled_model_actions and if the enabled_model_actions length is greater than 0
   * Scopes are 'disabled' when enabled_model_actions = [], and are enabled when they have their proper model action inside the array
   */

  function isCustomObjectsEnabledForCategory(category: string) {
    const filteredModels = commonModels.filter((model) => model.name.startsWith(category));
    return filteredModels.every(
      (model) => model.enabled_model_actions && model.enabled_model_actions.length > 0,
    );
  }

  const handleStatusToggle = (category: APICategory) => {
    updateCustomObjectCommonModel({
      category: category,
      isEnabled: isCustomObjectsEnabledForCategory(category),
    }).then((result) => {
      if (result.status === "success") {
        setCommonModels(result.data);
        return { status: "success", data: undefined };
      } else {
        showErrorToast(
          "Custom Objects are only available for customers on our Professional and Enterprise plans.",
        );
        return result;
      }
    });
  };

  return (
    <SectionHeaderWrapper
      title="Syncing Custom Objects"
      subtitle="Customize the syncing of your custom objects by category. Enabling a category allows you to read and write custom data models from your users' third-party platforms directly through Merge's Unified API, just like Common Models."
      docsUrl={DOCS_CUSTOM_OBJECTS_PATH}
    >
      <>
        <Card className="flex flex-col">
          <div className="flex flex-col px-6 pb-6 pt-5 border-b border-gray-10 gap-y-3">
            <Text variant="h5">Categories</Text>
            <Text>
              Enabling a category will sync{" "}
              <Link href={DOCS_API_REF_CUSTOM_OBJECTS_PATH} target="_blank">
                Custom Objects
              </Link>
              ,{" "}
              <Link href={DOCS_API_REF_CUSTOM_OBJECT_CLASSES_PATH} target="_blank">
                Custom Object Classes
              </Link>
              ,{" "}
              <Link href={DOCS_API_REF_ASSOCIATIONS_PATH} target="_blank">
                Associations
              </Link>
              , and{" "}
              <Link href={DOCS_API_REF_ASSOCIATION_TYPES_PATH} target="_blank">
                Association Types
              </Link>{" "}
              for all integrations in that category
            </Text>
          </div>

          {Object.entries<CategoryIsActiveConfig>(CategoryDisplayConfig).map(
            ([category, { isActive }]) => (
              <div className="flex flex-row items-center justify-between p-6 w-full border-b border-gray-10 last:border-b-0">
                {dataHasLoaded ? (
                  <>
                    <Text variant="h6">
                      {lucideIconForAPICategory(category as APICategory, "md", "mr-3 mb-[3px]")}
                      {displayNameForAPICategory(category as APICategory)}
                    </Text>

                    {isActive ? (
                      <Toggle
                        checked={isCustomObjectsEnabledForCategory(category)}
                        label={isCustomObjectsEnabledForCategory(category) ? "Enabled" : "Disabled"}
                        onChange={() => {
                          handleStatusToggle(category as APICategory);
                        }}
                      />
                    ) : (
                      <Badge>Coming soon</Badge>
                    )}
                  </>
                ) : (
                  <div className="flex flex-row items-center justify-between w-full">
                    <SkeletonLoader height={20} width={238} borderRadius={8} />
                    <SkeletonLoader height={20} width={182} borderRadius={8} />
                  </div>
                )}
              </div>
            ),
          )}
        </Card>
      </>
    </SectionHeaderWrapper>
  );
};

export default ConfigurationSyncingCustomObjects;
